// Anmeldetext-Vorlagen pflegen (Rich-Text-Umstellung, Teil 3)
//
// Wortgleiche Übertragung von PaymentNoteTemplateListPage.tsx auf den anderen
// Vorlagentyp. Siehe dort für die volle Begründung jeder Zusicherung.

import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { ArrowLeft, Pencil, Plus, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { ConfirmationDialog } from '@/components/ui/confirmation-dialog';
import { useToast } from '@/hooks/use-toast';
import { useMe } from '@/hooks/usePermissions';
import { ApiError, apiClient } from '@/lib/api-client';
import { cn } from '@/lib/utils';
import {
  useRegistrationNoteTemplates,
  useSaveRegistrationNoteTemplate,
  useDeleteRegistrationNoteTemplates,
} from '@/services/event-service';
import { formatDay } from '@/features/events/mailing-match';
import { Role, type RegistrationNoteTemplate, type SaveRegistrationNoteTemplateRequest } from '@/types';
import { RegistrationNoteTemplateFormDialog } from './RegistrationNoteTemplateFormDialog';
import { pruneSelection, selectionState, toggleAll, toggleOne } from './selection';

/** Textauszug für die Übersichtsspalte — Markdown-Quelltext ist als Klartext lesbar genug. */
function excerpt(text: string, max = 90): string {
  const plain = text.replace(/\s+/g, ' ').trim();

  return plain.length > max ? `${plain.slice(0, max)}…` : plain;
}

export function RegistrationNoteTemplateListPage() {
  const { t } = useTranslation('settings');
  const { toast } = useToast();

  const { data: me } = useMe();
  const canManage = me?.role === Role.Zentral || me?.role === Role.Admin;

  const { data: templates = [], isLoading, isError, error } = useRegistrationNoteTemplates();
  const saveMutation = useSaveRegistrationNoteTemplate();
  const deleteMutation = useDeleteRegistrationNoteTemplates();

  const [selected, setSelected] = useState<string[]>([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<RegistrationNoteTemplate | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<string[] | null>(null);
  const [usage, setUsage] = useState<{ count: number; available: boolean } | null>(null);

  const ids = useMemo(() => templates.map((template) => template.id), [templates]);

  const liveSelection = useMemo(() => pruneSelection(selected, ids), [selected, ids]);

  const headerState = selectionState(templates.length, liveSelection.length);
  const isDenied =
    error instanceof ApiError && (error.status === 401 || error.status === 403);

  useEffect(() => {
    if (!confirmDelete) {
      setUsage(null);
      return;
    }

    let abgebrochen = false;
    setUsage(null);

    apiClient
      .getRegistrationNoteTemplateUsage(confirmDelete)
      .then((antwort) => {
        if (abgebrochen) return;
        const summe = Object.values(antwort.usage).reduce((a, b) => a + b, 0);
        setUsage({ count: summe, available: antwort.available });
      })
      .catch(() => {
        if (!abgebrochen) setUsage({ count: 0, available: false });
      });

    return () => {
      abgebrochen = true;
    };
  }, [confirmDelete]);

  const openCreate = () => {
    setEditing(null);
    setSaveError(null);
    setDialogOpen(true);
  };

  const openEdit = (template: RegistrationNoteTemplate) => {
    setEditing(template);
    setSaveError(null);
    setDialogOpen(true);
  };

  const handleSave = (req: SaveRegistrationNoteTemplateRequest) => {
    setSaveError(null);
    saveMutation.mutate(req, {
      onSuccess: () => {
        toast({ title: t('registrationNoteTemplates.toast.saved') });
        setDialogOpen(false);
      },
      onError: (e) => setSaveError(e instanceof Error ? e.message : String(e)),
    });
  };

  const handleDelete = (doomed: string[]) => {
    deleteMutation.mutate(doomed, {
      onSuccess: (result) => {
        setConfirmDelete(null);
        setSelected([]);

        toast({
          title: t('registrationNoteTemplates.toast.deleted', { count: result.removed.length }),
        });

        if (result.failed.length > 0) {
          toast({
            title: t('registrationNoteTemplates.toast.deleteFailed'),
            description: result.failed.map((f) => `${f.id}: ${f.reason}`).join('\n'),
            variant: 'destructive',
          });
        }
      },
      onError: (e) =>
        toast({
          title: t('registrationNoteTemplates.toast.error'),
          description: e instanceof Error ? e.message : String(e),
          variant: 'destructive',
        }),
    });
  };

  if (!canManage) {
    return (
      <div className="space-y-6">
        <h2 className="text-3xl font-bold tracking-tight">
          {t('registrationNoteTemplates.page.title')}
        </h2>
        <p className="p-8 text-center text-muted-foreground">
          {t('registrationNoteTemplates.page.noAccess')}
        </p>
      </div>
    );
  }

  const titleOf = (id: string) => templates.find((template) => template.id === id)?.title ?? id;

  const deleteDescription = (): string => {
    if (!confirmDelete) return '';

    const kopf =
      confirmDelete.length === 1
        ? t('registrationNoteTemplates.delete.descriptionOne', { title: titleOf(confirmDelete[0]) })
        : t('registrationNoteTemplates.delete.descriptionMany', { count: confirmDelete.length });

    if (!usage) return kopf;
    if (!usage.available) {
      return `${kopf}\n\n${t('registrationNoteTemplates.delete.usageUnavailable')}`;
    }
    if (usage.count === 0) return kopf;

    return `${kopf}\n\n${t('registrationNoteTemplates.delete.usageHint', { count: usage.count })}`;
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between">
        <div className="space-y-1">
          <Button asChild variant="ghost" size="sm" className="-ml-2 h-7 gap-1 px-2">
            <Link to="/settings">
              <ArrowLeft className="h-3.5 w-3.5" />
              {t('registrationNoteTemplates.page.backToSettings')}
            </Link>
          </Button>
          <h2 className="text-3xl font-bold tracking-tight">
            {t('registrationNoteTemplates.page.title')}
          </h2>
          <p className="text-muted-foreground">{t('registrationNoteTemplates.page.description')}</p>
        </div>
        <Button className="gap-2" onClick={openCreate}>
          <Plus className="h-4 w-4" />
          {t('registrationNoteTemplates.actions.create')}
        </Button>
      </div>

      {isLoading ? (
        <p className="p-8 text-center text-muted-foreground">
          {t('registrationNoteTemplates.page.loading')}
        </p>
      ) : isDenied ? (
        <p className="p-8 text-center text-muted-foreground">
          {t('registrationNoteTemplates.page.noAccess')}
        </p>
      ) : isError ? (
        <p className="p-8 text-center text-red-600">{t('registrationNoteTemplates.page.loadError')}</p>
      ) : templates.length === 0 ? (
        <p className="rounded-md border p-8 text-center text-muted-foreground">
          {t('registrationNoteTemplates.page.empty')}
        </p>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-12">
                <Checkbox
                  checked={headerState === 'all'}
                  onCheckedChange={() => setSelected(toggleAll(ids, liveSelection))}
                  aria-label={t('registrationNoteTemplates.table.selectAll')}
                  className={cn(headerState === 'some' && 'data-[state=checked]:bg-primary/50')}
                />
              </TableHead>
              <TableHead>{t('registrationNoteTemplates.table.title')}</TableHead>
              <TableHead>{t('registrationNoteTemplates.table.preview')}</TableHead>
              <TableHead>{t('registrationNoteTemplates.table.modified')}</TableHead>
              <TableHead className="text-right">{t('registrationNoteTemplates.table.actions')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {templates.map((template) => {
              const isSelected = liveSelection.includes(template.id);

              return (
                <TableRow key={template.id} className={cn(isSelected && 'bg-blue-50')}>
                  <TableCell>
                    <Checkbox
                      checked={isSelected}
                      onCheckedChange={() => setSelected(toggleOne(liveSelection, template.id))}
                      aria-label={t('registrationNoteTemplates.table.selectRow', {
                        title: template.title,
                      })}
                    />
                  </TableCell>
                  <TableCell className="font-medium">{template.title}</TableCell>
                  <TableCell className="max-w-[28rem] text-sm text-muted-foreground">
                    {excerpt(template.text)}
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">
                    {formatDay(template.modifiedAt)}
                    {template.modifiedBy && <div className="truncate">{template.modifiedBy}</div>}
                  </TableCell>
                  <TableCell className="text-right">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => openEdit(template)}
                      aria-label={t('registrationNoteTemplates.actions.edit')}
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setConfirmDelete([template.id])}
                      aria-label={t('registrationNoteTemplates.actions.delete')}
                    >
                      <Trash2 className="h-4 w-4 text-red-600" />
                    </Button>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}

      {liveSelection.length > 0 && (
        <div className="sticky bottom-0 z-40 flex items-center justify-between gap-4 border-t bg-background/95 p-3 backdrop-blur">
          <span className="text-sm text-muted-foreground">
            {t('registrationNoteTemplates.bulk.selected', { count: liveSelection.length })}
          </span>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => setSelected([])}>
              {t('registrationNoteTemplates.bulk.clearSelection')}
            </Button>
            <Button
              variant="destructive"
              size="sm"
              className="gap-2"
              onClick={() => setConfirmDelete([...liveSelection])}
            >
              <Trash2 className="h-4 w-4" />
              {t('registrationNoteTemplates.bulk.deleteSelected')}
            </Button>
          </div>
        </div>
      )}

      <RegistrationNoteTemplateFormDialog
        open={dialogOpen}
        template={editing}
        saveError={saveError}
        onOpenChange={setDialogOpen}
        onSave={handleSave}
        isSaving={saveMutation.isPending}
      />

      <ConfirmationDialog
        open={confirmDelete !== null}
        onOpenChange={(open) => !open && setConfirmDelete(null)}
        title={t(
          confirmDelete?.length === 1
            ? 'registrationNoteTemplates.delete.title'
            : 'registrationNoteTemplates.delete.titleMany'
        )}
        description={deleteDescription()}
        confirmLabel={t('registrationNoteTemplates.delete.confirm')}
        cancelLabel={t('registrationNoteTemplates.form.cancel')}
        variant="destructive"
        onConfirm={() => confirmDelete && handleDelete(confirmDelete)}
      />
    </div>
  );
}
